import { useState } from 'react'
import { fmt } from '../data/format.js'
import { useManualContributor } from '../mutations/useManualContributor.js'
import { useIsReadOnly } from '../store/archiveViewStore.js'

/**
 * SubgoalContributorsDrawer — small expanded panel under a HubSpot-synced
 * sub-goal row showing the per-deal contributors. Populated from
 * `sub.contributors` (auto from HubSpot sync) + `sub.manualContributors`
 * (user-managed entries that survive syncs and contribute to current).
 *
 * Auto contributor shape (from worker):
 *   { dealId, dealname, company, productType, amount, closedate }
 *
 * Manual contributor shape:
 *   { id, company, productType, amount, closedate, note, isManual, addedAt }
 */

// Pill colors per canonical product type. Matches the line-item palette
// used elsewhere on the dashboard so the same product gets the same chip
// color whether it's on a deal or an invoice.
const PRODUCT_PILL = {
  'Design':         { bg: '#E1F5EE', fg: '#085041' },
  'Strategic Plan': { bg: '#EEEDFE', fg: '#26215C' },
  'End-to-end':     { bg: '#E6F1FB', fg: '#042C53' },
  'EO Planner':     { bg: '#EAF3DE', fg: '#173404' },
  'Trust Enforcer': { bg: '#FAEEDA', fg: '#633806' },
  'Advisory':       { bg: '#FBEAF0', fg: '#4B1528' },
  'Other':          { bg: '#F1EFE8', fg: '#2C2C2A' },
}

const fmtDate = (iso) => {
  if (!iso) return null
  const d = new Date(iso)
  if (isNaN(d.getTime())) return null
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

const dealUrl = (dealId) =>
  dealId
    ? `https://app.hubspot.com/contacts/24204346/record/0-3/${dealId}`
    : null

export default function SubgoalContributorsDrawer({
  contributors,
  manualContributors,
  pillarName,
  goalIndex,
  subgoalIndex,
}) {
  const auto = Array.isArray(contributors) ? contributors : []
  const manual = Array.isArray(manualContributors) ? manualContributors : []
  const rows = [...auto, ...manual.map((m) => ({ ...m, isManual: true }))]
  const readOnly = useIsReadOnly()
  const canEdit = !readOnly && typeof pillarName === 'string' && typeof goalIndex === 'number' && typeof subgoalIndex === 'number'
  const op = useManualContributor()
  const [showForm, setShowForm] = useState(false)
  const [form, setForm] = useState({ company: '', productType: 'Trust Enforcer', amount: '', closedate: '' })

  const submitManual = () => {
    if (!form.company.trim() || !form.amount) return
    op.add({
      pillarName,
      goalIndex,
      subgoalIndex,
      contributor: {
        company: form.company,
        productType: form.productType,
        amount: parseFloat(form.amount) || 0,
        closedate: form.closedate || null,
      },
    })
    setForm({ company: '', productType: 'Trust Enforcer', amount: '', closedate: '' })
    setShowForm(false)
  }

  const removeManual = (id) => {
    if (!id) return
    op.remove({ pillarName, goalIndex, subgoalIndex, contributorId: id })
  }

  if (!rows.length && !canEdit) return null

  return (
    <div
      style={{
        background: '#FAFAFA',
        border: '1px solid var(--border)',
        borderRadius: 8,
        margin: '0 12px 10px 36px',
        padding: '8px 12px',
        fontSize: 11,
      }}
    >
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1.4fr 110px 90px 90px',
          gap: 8,
          fontSize: 10,
          color: 'var(--muted)',
          textTransform: 'uppercase',
          letterSpacing: 0.05,
          paddingBottom: 4,
          borderBottom: '1px dashed var(--border)',
        }}
      >
        <div>Customer</div>
        <div>Product</div>
        <div style={{ textAlign: 'right' }}>Amount</div>
        <div>Signed</div>
      </div>
      {rows.map((c, i) => {
        const pill = PRODUCT_PILL[c.productType] || PRODUCT_PILL.Other
        const signed = fmtDate(c.closedate) || '—'
        const href = dealUrl(c.dealId)
        const isManual = !!c.isManual
        return (
          <div
            key={c.id || c.dealId || i}
            style={{
              display: 'grid',
              gridTemplateColumns: '1.4fr 110px 90px 90px',
              gap: 8,
              alignItems: 'center',
              padding: '5px 0',
              borderBottom: i === rows.length - 1 ? 'none' : '1px dashed var(--border)',
              fontSize: 11,
              background: isManual ? '#FFF7E6' : 'transparent',
              marginLeft: isManual ? -6 : 0,
              marginRight: isManual ? -6 : 0,
              paddingLeft: isManual ? 6 : 0,
              paddingRight: isManual ? 6 : 0,
              borderRadius: isManual ? 3 : 0,
              position: 'relative',
            }}
          >
            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 6 }} title={c.note || c.dealname || c.company}>
              {href ? (
                <a
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: 'var(--accent)', textDecoration: 'none' }}
                >
                  {c.company || c.dealname || 'Unknown'}
                </a>
              ) : (
                <span>{c.company || c.dealname || 'Unknown'}</span>
              )}
              {isManual && (
                <span
                  title="Added manually — not (yet) in HubSpot"
                  style={{
                    background: '#FAEEDA',
                    color: '#633806',
                    fontSize: 9,
                    fontWeight: 600,
                    padding: '1px 5px',
                    borderRadius: 3,
                    textTransform: 'uppercase',
                    letterSpacing: 0.04,
                  }}
                >
                  manual
                </span>
              )}
            </div>
            <div>
              <span
                style={{
                  background: pill.bg,
                  color: pill.fg,
                  padding: '1px 6px',
                  borderRadius: 3,
                  fontSize: 10,
                }}
              >
                {c.productType || 'Other'}
              </span>
            </div>
            <div
              style={{
                textAlign: 'right',
                fontVariantNumeric: 'tabular-nums',
                fontWeight: 500,
              }}
            >
              {fmt(c.amount, '$')}
            </div>
            <div style={{ color: 'var(--muted)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6 }}>
              <span>{signed}</span>
              {isManual && canEdit && (
                <button
                  type="button"
                  onClick={() => removeManual(c.id)}
                  title="Remove manual entry"
                  aria-label="Remove manual entry"
                  style={{
                    width: 16, height: 16, border: 'none', background: 'transparent',
                    color: 'var(--muted-light)', fontSize: 10, cursor: 'pointer', padding: 0,
                    borderRadius: 3, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.color = '#dc2626'; e.currentTarget.style.background = '#fee2e2' }}
                  onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--muted-light)'; e.currentTarget.style.background = 'transparent' }}
                >✕</button>
              )}
            </div>
          </div>
        )
      })}

      {canEdit && !showForm && (
        <div style={{ paddingTop: 6 }}>
          <button
            type="button"
            onClick={() => setShowForm(true)}
            style={{
              background: 'transparent', border: '1px dashed var(--border)',
              color: 'var(--muted)', fontSize: 11, padding: '3px 8px',
              borderRadius: 4, cursor: 'pointer',
            }}
          >+ Add manual entry</button>
        </div>
      )}

      {canEdit && showForm && (
        <div style={{ paddingTop: 8, display: 'grid', gridTemplateColumns: '1.4fr 110px 90px 90px auto', gap: 8, alignItems: 'center' }}>
          <input
            type="text"
            placeholder="Customer"
            value={form.company}
            onChange={(e) => setForm({ ...form, company: e.target.value })}
            style={{ fontSize: 11, padding: '4px 6px', border: '1px solid var(--border)', borderRadius: 3 }}
          />
          <select
            value={form.productType}
            onChange={(e) => setForm({ ...form, productType: e.target.value })}
            style={{ fontSize: 11, padding: '4px 6px', border: '1px solid var(--border)', borderRadius: 3 }}
          >
            {['Design', 'Strategic Plan', 'End-to-end', 'EO Planner', 'Trust Enforcer', 'Advisory', 'Other'].map((p) => (
              <option key={p} value={p}>{p}</option>
            ))}
          </select>
          <input
            type="number"
            placeholder="$"
            value={form.amount}
            onChange={(e) => setForm({ ...form, amount: e.target.value })}
            style={{ fontSize: 11, padding: '4px 6px', border: '1px solid var(--border)', borderRadius: 3, textAlign: 'right' }}
          />
          <input
            type="date"
            value={form.closedate}
            onChange={(e) => setForm({ ...form, closedate: e.target.value })}
            style={{ fontSize: 11, padding: '4px 6px', border: '1px solid var(--border)', borderRadius: 3 }}
          />
          <div style={{ display: 'flex', gap: 4 }}>
            <button
              type="button"
              onClick={submitManual}
              disabled={!form.company.trim() || !form.amount}
              style={{
                background: '#16A34A', color: '#fff', border: 'none',
                padding: '4px 10px', borderRadius: 3, fontSize: 11, cursor: 'pointer',
                opacity: !form.company.trim() || !form.amount ? 0.5 : 1,
              }}
            >Add</button>
            <button
              type="button"
              onClick={() => { setShowForm(false); setForm({ company: '', productType: 'Trust Enforcer', amount: '', closedate: '' }) }}
              style={{
                background: 'transparent', color: 'var(--muted)', border: '1px solid var(--border)',
                padding: '4px 8px', borderRadius: 3, fontSize: 11, cursor: 'pointer',
              }}
            >Cancel</button>
          </div>
        </div>
      )}
    </div>
  )
}
